import { ProgressCircle } from "@/components/ui/progress-circle.tsx";
import { ORPCError } from "@orpc/client";
import { markFlagContextResolved } from "@packages/core-client/hooks/flagContextResolution.ts";
import { useLoadingIndicator } from "@packages/core-client/hooks/useLoadingIndicator.tsx";
import { PREFERRED_LANGUAGE_STORAGE_KEY } from "@packages/core-client/i18n/createI18nextPlugin.ts";
import { ReturnToProvider } from "@packages/core-client/routing/useReturnTo.tsx";
import { SessionProvider } from "@packages/core-client/session/SessionContext.tsx";
import type { AppDeferredRegistrationData } from "@packages/core-client/registration/appModuleRegisterFunction.ts";
import { AppRouter as FireflyAppRouter, useDeferredRegistrations, useIsBootstrapping, useLaunchDarklyClient, useProtectedDataQueries } from "@squide/firefly";
import { useChangeLanguage, useCurrentLanguage, useI18nextInstance } from "@squide/i18next";
import { useIsBootstrapping as useIsWorkleapAiBootstrapping } from "@workleap-ai/client/react";
import { useHoneycombInstrumentationClient, useLogRocketInstrumentationClient, useMixpanelClient } from "@workleap/telemetry/react";
import LogRocket from "logrocket";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createBrowserRouter, Outlet } from "react-router";
import { RouterProvider } from "react-router/dom";
import { useAuthClient } from "./api/auth.ts";
import { RootErrorBoundary } from "./boundaries/RootErrorBoundary.tsx";
import { getRoleEventProperties } from "./getRoleEventProperties.ts";
import { i18NextInstanceKey } from "./i18next.ts";
import { OrganizationAccessPage } from "./pages/OrganizationAccessPage.tsx";
import { PageViewTracker } from "./PageViewTracker.tsx";

function BootstrappingRoute() {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("BootstrappingRoute", { i18n: i18nextInstance });

    const authClient = useAuthClient();

    const [session] = useProtectedDataQueries([authClient.me.queryOptions({ retry: 0 })], error => error instanceof ORPCError && error.status === 401);

    const changeLanguage = useChangeLanguage();

    useEffect(() => {
        if (session?.preferredLanguage) {
            try {
                localStorage.setItem(PREFERRED_LANGUAGE_STORAGE_KEY, session.preferredLanguage);
            } catch {}

            changeLanguage(session.preferredLanguage).catch(() => {});
        }
    }, [session, changeLanguage]);

    const locale = useCurrentLanguage();

    useEffect(() => {
        document.documentElement.lang = locale;
    }, [locale]);

    const deferredRegistrationData = useMemo<AppDeferredRegistrationData>(() => ({ session }), [session]);

    useDeferredRegistrations(deferredRegistrationData);

    const ldClient = useLaunchDarklyClient();
    const logRocketClient = useLogRocketInstrumentationClient({ throwOnUndefined: false });
    const honeycombClient = useHoneycombInstrumentationClient({ throwOnUndefined: false });
    const mixpanelClient = useMixpanelClient({ throwOnUndefined: false });

    useEffect(() => {
        if (session) {
            const organizationId = session.organizationId;
            let isCurrent = true;

            void Promise.resolve(
                ldClient.identify({
                    kind: "organization",
                    key: organizationId,
                    ...(session.organizationName && { name: session.organizationName })
                })
            )
                .catch(() => undefined)
                .finally(() => {
                    if (isCurrent) {
                        markFlagContextResolved(organizationId);
                    }
                });

            return () => {
                isCurrent = false;
            };
        }
    }, [session, ldClient]);

    useEffect(() => {
        if (session) {
            if (logRocketClient) {
                const traits = logRocketClient.createWorkleapPlatformDefaultUserTraits({
                    userId: session.memberId,
                    organizationId: session.organizationId,
                    organizationName: session.organizationName ?? "",
                    isMigratedToWorkleap: session.hasAccessToWorkleapNext === true,
                    isAdmin: session.isOrganizationAdmin,
                    isReportingManager: session.isReportingManager,
                    isTeamManager: session.isTeamManager
                }) as Record<string, string | number | boolean>;

                LogRocket.identify(session.memberId, traits);
            }

            if (honeycombClient) {
                honeycombClient.setGlobalSpanAttributes({
                    "app.user_id": session.email
                });
            }

            if (mixpanelClient) {
                mixpanelClient.setGlobalEventProperties({
                    App: "Workleap Next",
                    "Member ID": session.memberId,
                    "Organization ID": session.organizationId,
                    "Organization Name": session.organizationName,
                    "Network ID": session.organizationId,
                    ...getRoleEventProperties(session)
                });
            }
        }
    }, [session, logRocketClient, honeycombClient, mixpanelClient]);

    const hasNoAccess = session !== undefined && session.hasAccessToWorkleapNext !== true && session.isInWorkleapNextPreview !== true;
    const track = useMemo(() => mixpanelClient?.createTrackingFunction(), [mixpanelClient]);

    useEffect(() => {
        if (hasNoAccess) {
            void track?.("Organization Access Denied Viewed", { "Organization ID": session?.organizationId, "Member ID": session?.memberId });
        }
    }, [hasNoAccess, track, session?.organizationId, session?.memberId]);

    const isSquideBootstrapping = useIsBootstrapping();
    const isWorkleapAiBootstrapping = useIsWorkleapAiBootstrapping();
    const isBootstrapping = isSquideBootstrapping || isWorkleapAiBootstrapping;
    const shouldRenderSpinner = useLoadingIndicator(isBootstrapping);

    if (isBootstrapping && !shouldRenderSpinner) {
        return null;
    }

    if (shouldRenderSpinner) {
        return (
            <div className="flex min-h-dvh items-center justify-center">
                <ProgressCircle isIndeterminate aria-label={t("loading")} className="text-muted-foreground size-12" />
            </div>
        );
    }

    if (hasNoAccess) {
        return <OrganizationAccessPage />;
    }

    return (
        <SessionProvider session={session ?? null}>
            <PageViewTracker />
            <ReturnToProvider>
                <Outlet />
            </ReturnToProvider>
        </SessionProvider>
    );
}

export function AppRouter() {
    return (
        <FireflyAppRouter waitForProtectedData>
            {({ rootRoute, registeredRoutes, routerProviderProps }) => {
                return (
                    <RouterProvider
                        router={createBrowserRouter([
                            {
                                element: rootRoute,
                                errorElement: <RootErrorBoundary />,
                                children: [
                                    {
                                        element: (
                                            <NuqsAdapter>
                                                <BootstrappingRoute />
                                            </NuqsAdapter>
                                        ),
                                        children: registeredRoutes
                                    }
                                ]
                            }
                        ])}
                        {...routerProviderProps}
                    />
                );
            }}
        </FireflyAppRouter>
    );
}
